import { jsPDF } from 'jspdf'
import html2canvas from 'html2canvas'
import logoUrl from '../assets/images/cargo360-logo.png'
import { getShipmentRequester } from './shipmentHelpers'

const NAVY = '#133157'
const ORANGE = '#EE8525'

const numberToWords = (num) => {
  const n = Math.round(Number(num) || 0)
  if (n === 0) return 'Zero Rupees Only'
  const a = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
  ]
  const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
  const convert = (x) => {
    if (x < 20) return a[x]
    if (x < 100) return b[Math.floor(x / 10)] + (x % 10 ? ` ${a[x % 10]}` : '')
    if (x < 1000) return `${a[Math.floor(x / 100)]} Hundred${x % 100 ? ` ${convert(x % 100)}` : ''}`
    if (x < 100000) return `${convert(Math.floor(x / 1000))} Thousand${x % 1000 ? ` ${convert(x % 1000)}` : ''}`
    if (x < 10000000) return `${convert(Math.floor(x / 100000))} Lakh${x % 100000 ? ` ${convert(x % 100000)}` : ''}`
    return `${convert(Math.floor(x / 10000000))} Crore${x % 10000000 ? ` ${convert(x % 10000000)}` : ''}`
  }
  return `${convert(n)} Rupees Only`
}

const formatCurrency = (amount) => {
  const value = Number(amount) || 0
  return `PKR ${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}

const formatIssuedDate = (date) => {
  const d = date ? new Date(date) : new Date()
  if (Number.isNaN(d.getTime())) return '—'
  const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  const timePart = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${datePart} · ${timePart}`
}

const formatShortDate = (date) => {
  const d = date ? new Date(date) : null
  if (!d || Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

const extractCity = (location) => {
  if (!location) return '—'
  const cleaned = location.replace(/[,.\n]/g, ' ').trim()
  const parts = cleaned.split(/\s+/).filter(Boolean)
  if (!parts.length) return '—'
  // Prefer a trailing token that looks like a city name (letters only, length > 2)
  for (let i = parts.length - 1; i >= 0; i -= 1) {
    if (/^[A-Za-z]{3,}$/.test(parts[i])) return parts[i]
  }
  return parts[parts.length - 1]
}

const getInvoiceAmount = (shipment) => {
  const budget = parseFloat(shipment?.budget) || 0
  const discount = parseFloat(shipment?.totalAmount) || 0
  if (shipment?.DiscountRequest && discount) {
    return Math.max(budget - discount, 0)
  }
  return budget
}

const getVehicleNumber = (shipment) => {
  const vehicles = shipment?.Trucker?.Vehicles || shipment?.Vehicle || []
  if (Array.isArray(vehicles) && vehicles.length > 0) {
    return vehicles[0].registrationNumber || vehicles[0].vehicleNumber || vehicles[0].plateNumber || '—'
  }
  return shipment?.vehicleNumber || shipment?.registrationNumber || '—'
}

const sanitizeFileName = (name) => {
  const cleaned = (name || 'Customer')
    .toString()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
  return cleaned || 'Customer'
}

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const buildInvoiceHtml = ({ shipment, logoSrc }) => {
  const requester = getShipmentRequester(shipment)
  const customerName = requester.name || '—'
  const company = requester.company || '—'
  const email = requester.email || '—'
  const phone = requester.phone || '—'
  const pickup = shipment?.pickupLocation || '—'
  const drop = shipment?.dropLocation || '—'
  const cargoType = shipment?.cargoType || shipment?.description || '—'
  const vehicleType = shipment?.vehicleType || '—'
  const vehicleNo = getVehicleNumber(shipment)
  const driverName = shipment?.Driver?.name || '—'
  const driverPhone = shipment?.Driver?.phone || '—'
  const status = (shipment?.status || 'confirmed').replace(/_/g, ' ').toUpperCase()
  const amount = getInvoiceAmount(shipment)
  const amountText = formatCurrency(amount)
  const amountWords = numberToWords(amount)
  const issued = formatIssuedDate(shipment?.createdAt || shipment?.updatedAt)
  const bookingDate = formatShortDate(shipment?.bookingDate || shipment?.createdAt)
  const deliveryDate = formatShortDate(shipment?.deliveryDate || shipment?.bookingDate || shipment?.createdAt)
  const route = `${extractCity(pickup)} → ${extractCity(drop)}`
  const pickupCity = extractCity(pickup)
  const companyLine = (() => {
    if (company === '—') return pickup
    if (pickupCity !== '—' && !company.toLowerCase().includes(pickupCity.toLowerCase())) {
      return `${company} ${pickupCity}`
    }
    return company
  })()

  const iconPin = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 21s7-5.33 7-12a7 7 0 1 0-14 0c0 6.67 7 12 7 12z" stroke="#9aa6b5" stroke-width="1.8"/><circle cx="12" cy="9" r="2.2" stroke="#9aa6b5" stroke-width="1.8"/></svg>`
  const iconWeb = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="9" stroke="#9aa6b5" stroke-width="1.8"/><path d="M3 12h18M12 3c2.5 2.8 3.8 5.9 3.8 9S14.5 18.2 12 21C9.5 18.2 8.2 15.1 8.2 12S9.5 5.8 12 3z" stroke="#9aa6b5" stroke-width="1.8"/></svg>`
  const iconMail = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="5" width="18" height="14" rx="2" stroke="#9aa6b5" stroke-width="1.8"/><path d="M4 7l8 6 8-6" stroke="#9aa6b5" stroke-width="1.8"/></svg>`
  const iconPhone = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 3h3l1.5 4-2 1.5a12 12 0 0 0 6 6L17 12.5 21 14v3a2 2 0 0 1-2 2A16 16 0 0 1 5 5a2 2 0 0 1 2-2z" stroke="#9aa6b5" stroke-width="1.8" stroke-linejoin="round"/></svg>`

  return `
    <div id="cargo360-invoice-root" style="
      width: 794px;
      min-height: 1123px;
      background: #ffffff;
      color: ${NAVY};
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      position: relative;
      overflow: hidden;
      box-sizing: border-box;
    ">
      <!-- Top navy band + orange diagonal -->
      <div style="position:absolute;top:0;left:0;right:0;height:52px;background:${NAVY};"></div>
      <div style="
        position:absolute;top:0;right:0;width:0;height:0;
        border-top: 120px solid ${ORANGE};
        border-left: 200px solid transparent;
      "></div>

      <div style="position:relative;padding:64px 42px 20px;box-sizing:border-box;">
        <!-- Logo + Title (same row, below navy band) -->
        <div style="
          display:flex;align-items:center;justify-content:space-between;
          margin-bottom:28px;gap:16px;
        ">
          <img src="${logoSrc}" alt="Cargo360" style="width:92px;height:92px;object-fit:contain;display:block;flex-shrink:0;" />
          <div style="flex:1;text-align:center;">
            <h1 style="margin:0;font-size:36px;font-weight:700;color:${NAVY};letter-spacing:-0.4px;">Customer Invoice</h1>
            <p style="margin:10px 0 0;font-size:13px;color:#7a8799;">Issued: ${escapeHtml(issued)}</p>
          </div>
          <div style="width:92px;flex-shrink:0;"></div>
        </div>

        <!-- Summary bar -->
        <div style="
          background:#F4F7FB;border-radius:12px;padding:18px 22px;
          display:grid;grid-template-columns:1.3fr 1.4fr 0.8fr;gap:16px;margin-bottom:18px;
        ">
          <div>
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:6px;">CUSTOMER</div>
            <div style="font-size:15px;font-weight:700;color:${NAVY};line-height:1.3;">${escapeHtml(customerName)}</div>
            <div style="font-size:12px;color:#7a8799;margin-top:3px;line-height:1.35;">${escapeHtml(companyLine !== '—' ? companyLine : pickup)}</div>
          </div>
          <div>
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:6px;">SHIPMENT ROUTE</div>
            <div style="font-size:15px;font-weight:700;color:${NAVY};line-height:1.3;">${escapeHtml(route)}</div>
            <div style="font-size:12px;color:#7a8799;margin-top:3px;">Booking &amp; delivery: ${escapeHtml(deliveryDate !== '—' ? deliveryDate : bookingDate)}</div>
          </div>
          <div>
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:8px;">STATUS</div>
            <span style="
              display:inline-block;background:#E7F6EC;color:#2E7D4F;
              font-size:11px;font-weight:700;padding:6px 14px;border-radius:999px;letter-spacing:0.4px;
            ">${escapeHtml(status)}</span>
          </div>
        </div>

        <!-- Two info cards -->
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;">
          <div style="border:1.5px solid ${ORANGE};border-radius:12px;padding:16px 18px;">
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:12px;">CUSTOMER INFORMATION</div>
            ${infoRow('NAME', customerName)}
            ${infoRow('COMPANY', company)}
            ${infoRow('EMAIL', email)}
            ${infoRow('PHONE', phone)}
          </div>
          <div style="border:1.5px solid ${ORANGE};border-radius:12px;padding:16px 18px;">
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:12px;">VEHICLE &amp; DRIVER</div>
            ${infoRow('VEHICLE TYPE', vehicleType)}
            ${infoRow('VEHICLE NO.', vehicleNo)}
            ${infoRow('DRIVER', driverName)}
            ${infoRow('DRIVER PHONE', driverPhone)}
          </div>
        </div>

        <!-- Location & cargo -->
        <div style="border:1.5px solid ${ORANGE};border-radius:12px;padding:16px 18px;margin-bottom:16px;">
          <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:12px;">LOCATION &amp; CARGO DETAILS</div>
          <div style="display:grid;grid-template-columns:1.4fr 1fr;gap:10px 24px;">
            <div>
              ${infoRow('PICKUP', pickup)}
              ${infoRow('DROP-OFF', drop)}
              ${infoRow('CARGO DETAILS', cargoType)}
            </div>
            <div>
              ${infoRow('CARGO TYPE', cargoType)}
            </div>
          </div>
        </div>

        <!-- Service details -->
        <div style="border-radius:10px;overflow:hidden;border:1px solid #E4E9F0;margin-bottom:14px;">
          <div style="background:${NAVY};color:#fff;padding:10px 18px;font-size:12px;font-weight:700;letter-spacing:1.4px;">
            SERVICE DETAILS
          </div>
          <div style="padding:16px 18px;display:flex;justify-content:space-between;align-items:center;gap:16px;">
            <div>
              <div style="font-size:14px;font-weight:700;color:${NAVY};">Logistics transportation service</div>
              <div style="font-size:12px;color:#7a8799;margin-top:4px;">Confirmed Cargo360 transportation service</div>
            </div>
            <div style="font-size:16px;font-weight:700;color:${NAVY};white-space:nowrap;">${escapeHtml(amountText)}</div>
          </div>
        </div>

        <!-- Grand total -->
        <div style="
          background:#FFF3E9;border:1.5px solid ${ORANGE};border-radius:12px;
          padding:16px 20px;display:flex;justify-content:space-between;align-items:center;gap:16px;margin-bottom:18px;
        ">
          <div>
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:6px;">AMOUNT IN WORDS</div>
            <div style="font-size:15px;font-weight:700;color:${NAVY};">${escapeHtml(amountWords)}</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:4px;">GRAND TOTAL</div>
            <div style="font-size:28px;font-weight:800;color:${ORANGE};line-height:1;">${escapeHtml(amountText)}</div>
          </div>
        </div>

        <!-- Payment + notes -->
        <div style="display:grid;grid-template-columns:1.1fr 1fr;gap:20px;margin-bottom:28px;">
          <div>
            <div style="font-size:11px;font-weight:700;color:${ORANGE};letter-spacing:1.2px;margin-bottom:8px;">PAYMENT DETAILS</div>
            <div style="font-size:12.5px;color:${NAVY};line-height:1.7;">
              <div><strong>Bank:</strong> Meezan Bank</div>
              <div><strong>Account Name:</strong> CARGO 360 (PRIVATE) LIMITED</div>
              <div><strong>IBAN:</strong> PK26 MEZN 0002 6501 1298 1195</div>
              <div><strong>Account No.:</strong> 0265 0112981195</div>
            </div>
          </div>
          <div style="font-size:11.5px;color:#7a8799;line-height:1.55;padding-top:4px;">
            This invoice is generated for your confirmed Cargo360 shipment.
            It is system-generated and does not require signatures. Please
            retain a copy for your records.<br /><br />
            The Company shall not be responsible for any damage to the
            container caused during offloading.
          </div>
        </div>

        <!-- Footer -->
        <div style="border-top:1px solid #E4E9F0;padding-top:18px;padding-bottom:28px;position:relative;">
          <div style="display:grid;grid-template-columns:1.2fr 1fr;gap:16px;font-size:11.5px;color:#7a8799;padding:0 8px;">
            <div style="display:flex;flex-direction:column;gap:8px;">
              <div style="display:flex;align-items:flex-start;gap:8px;">
                <span style="display:inline-flex;margin-top:2px;">${iconPin}</span>
                <span>Plaza 146, 5 th floor, commercial area<br/>sector C Bahria town Lahore</span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="display:inline-flex;">${iconWeb}</span><span>www.cargo360pk.com</span>
              </div>
            </div>
            <div style="display:flex;flex-direction:column;gap:8px;">
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="display:inline-flex;">${iconMail}</span><span>info@cargo360pk.com</span>
              </div>
              <div style="display:flex;align-items:center;gap:8px;">
                <span style="display:inline-flex;">${iconPhone}</span><span>+92 333 7766609</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom orange diagonals -->
      <div style="
        position:absolute;bottom:0;left:0;width:0;height:0;
        border-bottom: 64px solid ${ORANGE};
        border-right: 120px solid transparent;
      "></div>
      <div style="
        position:absolute;bottom:0;right:0;width:0;height:0;
        border-bottom: 64px solid ${ORANGE};
        border-left: 120px solid transparent;
      "></div>
    </div>
  `
}

const infoRow = (label, value) => `
  <div style="display:grid;grid-template-columns:118px 1fr;gap:8px;margin-bottom:8px;align-items:start;">
    <div style="font-size:11px;font-weight:600;color:#8a95a5;letter-spacing:0.3px;">${escapeHtml(label)}</div>
    <div style="font-size:12.5px;font-weight:700;color:${NAVY};line-height:1.35;word-break:break-word;">${escapeHtml(value || '—')}</div>
  </div>
`

/**
 * Generate and download a Cargo360 customer invoice PDF for a shipment.
 * @param {Object} shipment - Raw shipment object from the API
 */
export const downloadShipmentInvoice = async (shipment) => {
  if (!shipment) throw new Error('Shipment data is required')

  const requester = getShipmentRequester(shipment)
  const companyName = sanitizeFileName(requester.company || requester.name || 'Customer')
  const fileName = `Cargo360_${companyName}_invoice.pdf`

  const host = document.createElement('div')
  host.style.cssText = 'position:fixed;left:-10000px;top:0;width:794px;background:#fff;z-index:-1;'
  host.innerHTML = buildInvoiceHtml({ shipment, logoSrc: logoUrl })
  document.body.appendChild(host)

  try {
    const node = host.querySelector('#cargo360-invoice-root')
    const canvas = await html2canvas(node, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      width: 794,
      windowWidth: 794,
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = pageWidth
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    // Fit to single A4 page
    const finalHeight = Math.min(imgHeight, pageHeight)
    const finalWidth = (canvas.width * finalHeight) / canvas.height
    const x = (pageWidth - finalWidth) / 2

    pdf.addImage(imgData, 'PNG', x, 0, finalWidth, finalHeight, undefined, 'FAST')
    pdf.save(fileName)
    return fileName
  } finally {
    document.body.removeChild(host)
  }
}
